import { getEnv } from '~/lib/env'
import { getDeviceToken } from '~/lib/deviceToken'
import { dataServerKeyFor } from '~/lib/nativeConfig'
import { getCurrentLocale } from '~/i18n'

function joinUrl(base: string, path: string) {
  const b = base.replace(/\/$/, '')
  const p = path.startsWith('/') ? path : `/${path}`
  return `${b}${p}`
}

function resolveUrl(url: string) {
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url
  }
  const { apiBaseUrl } = getEnv()
  const base = apiBaseUrl.replace(/\/$/, '')
  if (base) return joinUrl(base, url)
  return url.startsWith('/') ? url : `/${url}`
}

async function parseBody(res: Response): Promise<unknown> {
  const text = await res.text()
  if (!text) {
    return null
  }
  try {
    return JSON.parse(text) as unknown
  } catch {
    return text
  }
}

/**
 * Ошибка HTTP-запроса. Несёт `status` (реальный HTTP-код, 0 — сетевая ошибка)
 * и `body` (разобранное тело ответа) — чтобы UI мог показать в раскрытом тосте
 * код ошибки и пофилдовые сообщения валидации DRF.
 */
export class ApiRequestError extends Error {
  status: number
  body: unknown
  constructor(message: string, status: number, body: unknown) {
    super(message)
    this.name = 'ApiRequestError'
    this.status = status
    this.body = body
  }
}

function messageFromBody(data: unknown, fallback: string): string {
  if (data && typeof data === 'object') {
    const o = data as Record<string, unknown>
    const detail = o.detail
    if (typeof detail === 'string') {
      return detail
    }
    // Часть scheduling-views/identity-views возвращают `{"error": "..."}` —
    // обрабатываем наравне с `detail` / `non_field_errors` / `message`.
    const error = o.error
    if (typeof error === 'string') {
      return error
    }
    const nfe = o.non_field_errors
    if (Array.isArray(nfe) && typeof nfe[0] === 'string') {
      return nfe[0]
    }
    const msg = o.message
    if (typeof msg === 'string') {
      return msg
    }
  }
  return fallback
}

/**
 * Заголовки авторизации запроса; возвращает режим `credentials` для fetch.
 *
 * Запрос на сервер данных нативного ТСД (сторонний, не ritmflow — см.
 * `nativeConfig`) несёт только ключ терминала: токены ritmflow туда не уходят,
 * cookie не шлём.
 */
function applyAuth(
  headers: Record<string, string>,
  fullUrl: string,
  tokenForHeader: string | null,
  options: ApiRequestOptions,
): RequestCredentials {
  const dataKey = dataServerKeyFor(fullUrl)
  if (dataKey) {
    headers[dataKey.header] = dataKey.key
    return 'omit'
  }
  if (tokenForHeader) {
    headers.Authorization = `Token ${tokenForHeader}`
  } else if (options.skipAuth !== true) {
    const dt = getDeviceToken()
    if (dt) headers.Authorization = `Device ${dt}`
  }
  return 'include'
}

export type ApiRequestOptions = {
  /** Explicit token; default reads sessionStorage `token` for GET (optional). */
  token?: string | null
  /** Skip attaching session token (e.g. login POST). */
  skipAuth?: boolean
}

/**
 * HTTP helper (ritmpark useApi-style): JSON in/out, throws on network or non-2xx.
 * 4xx/5xx are errors (unlike axios example that swallowed 4xx as success).
 *
 * Если `params` — FormData / Blob / ArrayBuffer / URLSearchParams, тело
 * отправляется как есть, а `Content-Type` устанавливает сам браузер
 * (для FormData это нужно из-за multipart-boundary). Для GET FormData
 * не поддерживается — параметры строятся из `Record`.
 */
export async function apiRequest<T>(
  method: string,
  url: string,
  params: unknown = {},
  options: ApiRequestOptions = {},
): Promise<T> {
  const m = method.toUpperCase()
  const isGet = m === 'GET'
  const headers: Record<string, string> = {
    Accept: 'application/json',
    // Сообщает бэкенду, на каком языке возвращать сообщения и `verbose_name`.
    // Django `LocaleMiddleware` подхватит этот заголовок автоматически.
    'Accept-Language': getCurrentLocale(),
  }

  const sessionToken =
    typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('token') : null
  const tokenForHeader =
    options.skipAuth === true ? (options.token ?? null) : (options.token ?? sessionToken)

  if (isGet) {
    const qs = new URLSearchParams()
    if (params && typeof params === 'object' && !(params instanceof FormData)) {
      for (const [k, v] of Object.entries(params as Record<string, unknown>)) {
        if (v === undefined || v === null) {
          continue
        }
        qs.set(k, String(v))
      }
    }
    const q = qs.toString()
    const base = resolveUrl(url)
    const fullUrl = q ? `${base}${base.includes('?') ? '&' : '?'}${q}` : base
    const credentials = applyAuth(headers, fullUrl, tokenForHeader, options)
    const res = await fetch(fullUrl, {
      method: m,
      headers,
      credentials,
    })
    const data = await parseBody(res)
    if (!res.ok) {
      throw new ApiRequestError(
        messageFromBody(data, `${m} ${fullUrl} failed: ${res.status}`),
        res.status,
        data,
      )
    }
    return data as T
  }

  const fullUrl = resolveUrl(url)
  const credentials = applyAuth(headers, fullUrl, tokenForHeader, options)

  // FormData / Blob / ArrayBuffer / URLSearchParams отправляем как есть —
  // Content-Type выставит fetch сам (с правильным boundary для multipart).
  const isRawBody =
    typeof FormData !== 'undefined' && params instanceof FormData
    || typeof Blob !== 'undefined' && params instanceof Blob
    || typeof URLSearchParams !== 'undefined' && params instanceof URLSearchParams
    || (typeof ArrayBuffer !== 'undefined' && params instanceof ArrayBuffer)

  let body: BodyInit | undefined
  if (isRawBody) {
    body = params as BodyInit
  } else {
    headers['Content-Type'] = 'application/json'
    body = JSON.stringify(params ?? {})
  }

  const res = await fetch(fullUrl, {
    method: m,
    headers,
    body,
    credentials,
  })
  const data = await parseBody(res)
  if (!res.ok) {
    throw new ApiRequestError(
      messageFromBody(data, `${m} ${fullUrl} failed: ${res.status}`),
      res.status,
      data,
    )
  }
  return data as T
}
